<template>
  <div class="gen-container">
    <div class="gen-card">
      <div v-if="!hasFailed" class="verification-spinner">
        <svg style="width: 3rem; height: 3rem; margin: 0 auto; color: var(--lime)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      </div>

      <div>
        <h2 class="gateway-title">{{ t('verification.title') }}</h2>
        <p class="gen-subtitle">{{ statusMessage }}</p>
      </div>

      <div v-if="store.error" class="alert-error">
        {{ store.error }}
      </div>

      <div v-if="hasFailed" class="gen-retry">
        <div v-if="retriesRemaining > 0">
          <p class="gen-subtitle">{{ t('verification.retriesRemaining', { count: retriesRemaining }) }}</p>
          <button class="gen-btn-primary" :disabled="isRetrying" @click="retryGeneration">
            {{ isRetrying ? t('verification.retrying') : t('verification.retryButton') }}
          </button>
        </div>
        <div v-else>
          <p class="gen-subtitle">{{ t('verification.retriesExhausted') }}</p>
          <button class="gen-btn-secondary" @click="store.setStep('landing')">
            {{ t('verification.backToStart') }}
          </button>
        </div>
      </div>

      <div v-if="!hasFailed" class="gen-status">
        <span>{{ t('verification.status') }}: {{ currentStatus }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app';
import type { VehicleReportOutcome } from '@/services/api';
import { trackEvent } from '@/utils/analytics';

const { t, locale } = useI18n();
const store = useAppStore();

const statusMessage = ref(t('verification.querying', { make: 'Tesla', vin: '...' }));
const currentStatus = ref('GENERATING');
const hasFailed = ref(false);
const isRetrying = ref(false);
const retriesRemaining = ref(5);
const MAX_RETRIES = 5;

function describeOutcomeWarning(outcomes: Record<string, VehicleReportOutcome>): string | null {
  for (const outcome of Object.values(outcomes)) {
    if (outcome.core_status !== 'partial') continue;
    const failedModule = Object.entries(outcome.modules).find(([moduleName, value]) => (
      value.status === 'empty'
      && value.error_detail
      && moduleName !== 'vehicle_state'
      && value.error_detail !== 'drive_state_missing'
    ));
    if (failedModule) {
      const [moduleName, value] = failedModule;
      return `Partial vehicle data: ${moduleName} (${value.error_detail})`;
    }
    return null;
  }
  return null;
}

async function attemptGeneration() {
  const sessionId = store.sessionId;
  if (!sessionId) {
    store.setError('Session data is missing. Please try again.');
    store.setStep('landing');
    return;
  }

  statusMessage.value = t('verification.querying', { make: 'Tesla', vin: '...' });
  currentStatus.value = 'GENERATING_REPORTS';
  hasFailed.value = false;
  store.setError(null);

  try {
    // Store action fetches + persists typed reports/outcomes; we drive navigation here.
    const { result, typedReports, failedReports } = await store.generateVehicleReports(locale.value);

    if (result.status === 'retryable') {
      const serverRetryCount: number = result.retry_count ?? 0;
      retriesRemaining.value = MAX_RETRIES - serverRetryCount;
      store.setError(result.error || 'Report generation failed. Please retry.');
      statusMessage.value = t('verification.generationFailed');
      hasFailed.value = true;
      isRetrying.value = false;
      return;
    }

    if (result.status === 'complete' && result.reports) {
      const reportKeys = Object.keys(result.reports);
      if (reportKeys.length > 0) {
        // GA4 report compiled (spec 049-google-analytics, Task 3.5). The report
        // keys are VINs; report only the first 3 chars (never the full VIN, a
        // personal identifier). No-op until consent.
        trackEvent('generate_report', { vin_prefix: reportKeys[0].slice(0, 3) });
        const typedReportKeys = Object.keys(typedReports);
        if (typedReportKeys.length > 0) {
          store.setVehicleReport(typedReports[typedReportKeys[0]]);
          if (failedReports.length > 0) {
            store.setError(failedReports[0]);
          } else if (result.outcomes) {
            store.setError(describeOutcomeWarning(result.outcomes));
          }
          store.setStep('report');
        } else {
          // All reports failed but retries exhausted — go to landing
          store.setError(failedReports[0] || 'No reports were generated.');
          store.setStep('landing');
        }
      } else {
        store.setError('No reports were generated.');
        store.setStep('landing');
      }
    } else {
      store.setError('Report generation failed. Please try again.');
      store.setStep('landing');
    }
  } catch (error) {
    console.error('[GeneratingStep] Failed to generate reports:', error);
    const message = error instanceof Error ? error.message : 'Failed to generate vehicle report.';
    store.setError(message);
    retriesRemaining.value -= 1;
    if (retriesRemaining.value > 0) {
      statusMessage.value = t('verification.generationFailed');
      hasFailed.value = true;
      isRetrying.value = false;
    } else {
      store.setStep('landing');
    }
  }
}

async function retryGeneration() {
  isRetrying.value = true;
  await attemptGeneration();
}

onMounted(async () => {
  await attemptGeneration();
});
</script>

<style scoped>
.gen-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - var(--nav-height));
  padding: 2rem 1rem;
  background: var(--bg);
}

.gen-card {
  background: var(--surface-dark-new, #0a1f2f);
  border: 1px solid var(--border-dark-new, #183040);
  border-radius: 1rem;
  padding: 2.5rem 2rem;
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: center;
}

.gateway-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--white, #fff);
  margin: 0 0 0.5rem;
}

.gen-subtitle {
  color: var(--gray, #9ca3af);
  font-size: 0.95rem;
  margin: 0;
}

.gen-status {
  background: rgba(255,255,255,0.05);
  border: 1px solid var(--border-dark-new, #183040);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-family: monospace;
  font-size: 0.75rem;
  color: var(--gray, #9ca3af);
  text-align: left;
}

.alert-error {
  background: rgba(239,68,68,0.12);
  border: 1px solid rgba(239,68,68,0.3);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  color: #fca5a5;
  font-size: 0.875rem;
}

.gen-retry {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.gen-btn-primary {
  background: var(--lime, #c8f135);
  color: #071929;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
}

.gen-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.gen-btn-secondary {
  background: transparent;
  color: var(--white, #fff);
  border: 1px solid var(--border-dark-new, #183040);
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.95rem;
  cursor: pointer;
}

.verification-spinner {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}
</style>
